import { useTranslation } from 'react-i18next';
import React, { useMemo, useCallback } from 'react';
import message from 'antd/es/message';

import { IconHiOperateMenuIcon } from '@/assets/hi';
import { Dropdown, DropdownProps } from 'components';
import { useReport, useSwitch } from '@/hooks';

import { usePinSession, useArchiveSession } from '../../hooks/hiSession';
import { openArchiveSessionModal, openReportHiSessionModal } from '../Modals';
import { HiSession } from '../../types';
import { BlockModal } from '@/views/Stream/components/FeedMoreAction/ConfirmModal';
import style from './index.module.css';

type HiOperateMenuProps = {
  config: {
    otherSideHiId: string;
    otherSideName: string;
    sessionId: HiSession['id'];
    isBlocked?: boolean;
  };
  menuTypeList?: HiOperateMenuType[];
} & DropdownProps;

export enum HiOperateMenuType {
  PIN = 'PIN',
  UNPIN = 'UNPIN',
  ADDRESS_LABEL = 'ADDRESS_LABEL',
  ARCHIVE = 'ARCHIVE',
  REPORT = 'REPORT',
}
const HiOperateMenu = (props: HiOperateMenuProps) => {
  const { t } = useTranslation();

  const { menuTypeList = [], config, children, ...rest } = props;
  const { otherSideHiId, otherSideName, sessionId, isBlocked } = config;
  const { mutateAsync: pinSession } = usePinSession();
  const { mutateAsync: archiveSession } = useArchiveSession();
  const { mutateAsync: reportSession } = useReport(String(sessionId), 'hi_session');
  const { on: blockShown, turnOn: showBlock, turnOff: hideBlock } = useSwitch();

  const pinCallback = useCallback(
    async (pinned: boolean) => {
      pinSession({ pinned, sessionId }).then((is_success: boolean) => {
        if (is_success) {
          message.success(`${pinned ? 'Pin' : 'Unpin'} success`);
        }
      });
    },
    [pinSession, sessionId],
  );
  const archiveCallback = useCallback(async () => {
    archiveSession({ sessionId, otherSideHiId }).then((is_success: boolean) => {
      if (is_success) {
        message.success('Archive success');
      }
    });
  }, [archiveSession, otherSideHiId, sessionId]);

  const reportCallback = useCallback(
    async (reason?: string) => {
      reportSession(reason || '');
    },
    [reportSession],
  );

  const menuMap = useMemo(() => {
    return {
      [HiOperateMenuType.PIN]: {
        label: t('HiOperateMenu.1px1'),
        onClick: () => {
          pinCallback(true);
        },
      },
      [HiOperateMenuType.UNPIN]: {
        label: t('HiOperateMenu.1cr4n0'),
        onClick: () => {
          pinCallback(false);
        },
      },
      [HiOperateMenuType.ADDRESS_LABEL]: {
        label: isBlocked ? t('FeedMoreAction.unblock') : t('FeedMoreAction.block'),
        onClick: showBlock,
      },
      [HiOperateMenuType.ARCHIVE]: {
        label: t('HiOperateMenu.f5ovxe'),
        onClick: () => {
          openArchiveSessionModal({
            otherSideName,
            onConfirm: archiveCallback,
          });
        },
      },
      [HiOperateMenuType.REPORT]: {
        label: t('HiOperateMenu.14fa3x0'),
        onClick: () => {
          openReportHiSessionModal({
            onConfirm: reportCallback,
          });
        },
      },
    };
  }, [archiveCallback, otherSideName, pinCallback, reportCallback, showBlock, t, isBlocked]);

  const menus = useMemo(() => menuTypeList.map(type => menuMap[type]), [menuMap, menuTypeList]);

  return (
    <>
      <div className={style.container}>
        <Dropdown
          overlayClassName={style.menuDropdown}
          getPopupContainer={dom => dom.parentElement!}
          disabled={menus.length === 0}
          inParent
          data={menus}
          trigger={['click']}
          placement={'bottomRight'}
          {...rest}>
          {children ? children : <IconHiOperateMenuIcon className={style.operateMenuIcon} />}
        </Dropdown>
      </div>
      <BlockModal
        on={blockShown}
        turnOff={hideBlock}
        isBlocked={isBlocked}
        userId={otherSideHiId}
        userName={otherSideName}
      />
    </>
  );
};

export default HiOperateMenu;
